import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { ChangeLogMonitorService } from '../../../services/change-log-monitor.service';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-rerun-xml-generation',
    templateUrl: './rerun-xml-generation.component.html',
    styleUrls: ['./rerun-xml-generation.component.scss', '../../../slt-dashboard.styles.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RerunXmlGenerationComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  modalTitle = "Re Run XML Generation";
  minimise = false;
  isSaveClicked = false;

  // Component-specific properties
  saveloader = false;
  job_id: string[] = [];

  // Data properties
  baseURLs: any = {};
  headerData: any = {};
  rowData: any = {};
  filterParams: any = {};
  gridData: any[] = [];
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  globalService: any = {};

  private readonly subs = new Subscription();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private changeLogMonitorService: ChangeLogMonitorService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.headerData = header;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    const g = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = Array.isArray(g) ? g : g != null ? [g] : [];
    if (this.gridData.length) {
      this.processGridData();
    }
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  init() {
    try {
      // Initial setup if needed
    } catch (e) {
      console.error('Error in RerunXmlGeneration init:', e);
    }
  }

  processGridData() {
    // Process grid data to extract job IDs
    this.job_id = [];

    this.gridData.forEach((item: any) => {
      const data = item.data || item;
      this.job_id.push(data.JOB_ID);
    });

    this.cdr.detectChanges();
  }

  removeTag(index: number) {
    this.job_id.splice(index, 1);
    this.cdr.detectChanges();
  }

  regenerateProcess() {
    this.isSaveClicked = true;
    this.saveloader = true;
    this.cdr.detectChanges();

    // Build checkLockedbj from gridData - use real data from the first item
    const firstItem = this.gridData[0]?.data || this.gridData[0] || {};
    const checkLockedbj = [{
      "leid": firstItem.LEID || this.job_id[0] // Use LEID from first grid item or first job_id
    }];

    const gp = this.globalService?.globalParams || {};

    // Build params similar to AngularJS - merge globalParams with specific params
    // NOTE: The only difference from PDF is xml_pdf_flag: "XML" instead of "PDF"
    const params = {
      ...gp,
      job_id: this.job_id.toString(),
      xml_pdf_flag: "XML",
      scenarioCode: this.filterParams.scenarioCode || this.filterParams.scenario_code || gp?.scenarioCode || "RAF",
      is_issue_key_enabled: "N",
      checkLockedbj: JSON.stringify(checkLockedbj)
    };

    this.changeLogMonitorService.saveRerunXmlGeneration(
      this.GENERAL_CONFIG.base_url,
      params
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.saveloader = false;

        if (response.callSuccess == "1") {
          this._snackBar.open('Re run XML Generation process executed successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        } else {
          const errorMessage = response.message || response.data?.message || 'Error occurred while re-running XML generation';
          this._snackBar.open(errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }

        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error re-running XML generation:', error);
        this.isSaveClicked = false;
        this.saveloader = false;
        this._snackBar.open('Error occurred while re-running XML generation. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
